import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Share2, Twitter, Facebook, Linkedin, Link as LinkIcon, Check, Mail } from "lucide-react";
import { toast } from "sonner";

export default function ShareButtons({ title, url, summary, className = "" }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || window.location.href;
  const shareTitle = encodeURIComponent(title || "");
  const shareText = encodeURIComponent(summary || title || "");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const links = [
    {
      name: "Twitter / X",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${encodeURIComponent(shareUrl)}`,
      color: "hover:text-sky-500",
    },
    {
      name: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      color: "hover:text-blue-600",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      color: "hover:text-blue-700",
    },
    {
      name: "Email",
      icon: Mail,
      href: `mailto:?subject=${shareTitle}&body=${shareText}%0A%0A${encodeURIComponent(shareUrl)}`,
      color: "hover:text-red-500",
    },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`font-sans text-xs gap-1.5 ${className}`} data-testid="share-popover-trigger">
          <Share2 className="h-3.5 w-3.5" /> Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <p className="text-[10px] font-sans font-bold uppercase tracking-[0.15em] text-muted-foreground px-2 py-1.5">Share this article</p>
        {links.map(link => (
          <a
            key={link.name}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 px-2 py-2 text-sm font-sans text-foreground rounded-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${link.color}`}
            data-testid={`share-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <link.icon className="h-4 w-4" />
            {link.name}
          </a>
        ))}
        <button
          onClick={copyLink}
          className="flex items-center gap-3 px-2 py-2 text-sm font-sans text-foreground rounded-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors w-full hover:text-[#1E88E5]"
          data-testid="share-copy-link"
        >
          {copied ? <Check className="h-4 w-4 text-green-500" /> : <LinkIcon className="h-4 w-4" />}
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
